import { createTheme, type Theme } from '@mui/material/styles';
import { fonts, palette, radii } from './tokens';

/**
 * Shared outlined-button styling, applied to every colour variant.
 *
 * Written with CSS variables rather than a callback so it resolves per colour
 * scheme without a re-render - `text.primary` is ink in light and cream in dark,
 * and both clear AA against their own background.
 */
const outlinedButton = {
  color: 'var(--mui-palette-text-primary)',
  borderColor: 'var(--mui-palette-text-primary)',
  borderWidth: '1.5px',
  '&:hover': {
    borderColor: 'var(--mui-palette-text-primary)',
    borderWidth: '1.5px',
    backgroundColor: 'var(--mui-palette-primary-main)',
    color: 'var(--mui-palette-primary-contrastText)',
  },
} as const;

/**
 * The Kando MUI theme.
 *
 * Built with `cssVariables` + `colorSchemes` so light and dark are resolved by CSS
 * custom properties rather than a React re-render - no flash on first paint, and
 * the scheme can follow the operating system without JavaScript.
 */
export const theme: Theme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data-color-scheme',
  },

  colorSchemes: {
    light: {
      palette: {
        primary: {
          // Yellow with ink on top - 12:1. Note contrastText is deliberately dark:
          // white on yellow would be 1.4:1 and illegible.
          main: palette.sun,
          light: palette.sunPale,
          dark: palette.sunDeep,
          contrastText: palette.ink,
        },
        secondary: {
          // The readable accent. Yellow cannot be used for text on a light
          // background, so anything that must be *read* in an accent colour is this.
          main: palette.ember,
          light: palette.emberBright,
          dark: '#8F3612',
          contrastText: palette.paper,
        },
        background: {
          default: palette.paper,
          paper: palette.paperSunk,
        },
        text: {
          primary: palette.ink,
          secondary: palette.inkSoft,
        },
        divider: palette.line,
      },
    },

    dark: {
      palette: {
        primary: {
          main: palette.sun,
          light: '#FFE9B5',
          dark: palette.sunDeep,
          contrastText: palette.ink,
        },
        secondary: {
          main: palette.emberBright,
          light: '#FF8B68',
          dark: palette.ember,
          contrastText: palette.night,
        },
        background: {
          default: palette.night,
          paper: palette.nightRaised,
        },
        text: {
          primary: '#EDE7DA',
          secondary: '#A9A093',
        },
        divider: palette.nightLine,
      },
    },
  },

  typography: {
    fontFamily: fonts.body,

    // Display sizes use clamp() so headings scale with the viewport without
    // needing a breakpoint for every step.
    h1: {
      fontFamily: fonts.display,
      fontWeight: 500,
      fontSize: 'clamp(2.75rem, 1.6rem + 4.2vw, 5rem)',
      lineHeight: 1.05,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: fonts.display,
      fontWeight: 500,
      fontSize: 'clamp(2rem, 1.4rem + 2.4vw, 3.25rem)',
      lineHeight: 1.12,
      letterSpacing: '-0.015em',
    },
    h3: {
      fontFamily: fonts.display,
      fontWeight: 500,
      fontSize: 'clamp(1.5rem, 1.2rem + 1.2vw, 2.125rem)',
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: fonts.body,
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.35,
      letterSpacing: '-0.005em',
    },
    h5: {
      fontFamily: fonts.body,
      fontWeight: 600,
      fontSize: '1.0625rem',
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: fonts.body,
      fontWeight: 600,
      fontSize: '0.9375rem',
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: '1.1875rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    body1: {
      fontSize: '1.0625rem',
      lineHeight: 1.65,
    },
    body2: {
      fontSize: '0.9375rem',
      lineHeight: 1.6,
    },
    /** Small caps eyebrow above section headings. */
    overline: {
      fontFamily: fonts.mono,
      fontSize: '0.75rem',
      fontWeight: 500,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      lineHeight: 1.4,
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.01em',
      textTransform: 'none',
    },
  },

  shape: {
    borderRadius: radii.md,
  },

  components: {
    MuiTypography: {
      defaultProps: {
        // MUI maps subtitle1/subtitle2 to <h6> by default, which silently inserts
        // headings into the document outline wherever a standfirst appears. Screen
        // reader users navigating by heading then hit paragraphs of body copy.
        variantMapping: {
          subtitle1: 'p',
          subtitle2: 'p',
          body1: 'p',
          body2: 'p',
        },
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          scrollBehavior: 'smooth',
        },
        body: {
          // Optical sizing lets Newsreader/Inter adjust their design per size.
          fontOpticalSizing: 'auto',
        },
        // Respect users who have asked the OS to reduce motion.
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          // Fully rounded. The single strongest "approachable" signal available
          // for the cost of one line.
          borderRadius: radii.pill,
          paddingInline: '1.25rem',
          paddingBlock: '0.6rem',
        },
        sizeLarge: {
          paddingInline: '1.75rem',
          paddingBlock: '0.85rem',
          fontSize: '1rem',
        },

        // A pastel fill on a warm off-white is only 1.31:1 against the page, so a
        // contained button has almost no luminance edge to define its shape. The
        // label carries the contrast (ink on sun is 13.3:1); this border carries
        // the outline. Without it the control reads as a smudge rather than a
        // shape, and disappears entirely on a low-quality display.
        contained: {
          border: '1.5px solid var(--mui-palette-primary-dark)',
        },

        // Same trap as MuiLink, and the one axe caught: a text button draws its
        // label in `primary`, so "All services ->" rendered as yellow on the sunk
        // surface at 1.26:1. These read as links, so they take the link colour.
        text: { color: 'var(--mui-palette-secondary-main)' },
        textPrimary: { color: 'var(--mui-palette-secondary-main)' },
        textSecondary: { color: 'var(--mui-palette-secondary-main)' },

        // MUI's default outlined button draws its text and border in `primary`,
        // which here is yellow: 1.31:1 as text on paper and less as a border, so
        // the control is effectively invisible in the light scheme. Ink carries the
        // outline instead, and the brand yellow arrives on hover as a fill - where
        // ink on yellow is 13.3:1.
        outlined: outlinedButton,
        outlinedPrimary: outlinedButton,
        outlinedSecondary: outlinedButton,
      },
    },

    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          textUnderlineOffset: '0.2em',
          // MUI links default to `primary`, which is yellow here - 1.31:1 on paper
          // and effectively invisible. Links use the readable accent instead:
          // 5.2:1 in light, 5.9:1 in dark.
          color: 'var(--mui-palette-secondary-main)',

          // WCAG 1.4.1: a link inside running text must be identifiable without
          // colour. Ember against muted body copy is only 1.37:1, so hover-only
          // underlining leaves colour doing the work alone. Standalone links - the
          // footer columns, the nav - are not inside a text block and keep the
          // hover behaviour, which is why this is scoped to `p` rather than global.
          'p &': { textDecoration: 'underline' },
        },
      },
    },

    MuiContainer: {
      defaultProps: {
        maxWidth: 'lg',
      },
    },

    // A visible focus ring on every interactive surface - keyboard users must
    // always be able to see where they are.
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: '2px solid currentColor',
            outlineOffset: '2px',
          },
        },
      },
    },
  },
});
